import { useEffect, useRef, useState } from "react";
import { fieldErrorsOf } from "@repo/api-client";
import type { StaffBookingResponse, StaffCreateBookingRequest } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../shell/VenueContext";
import { StatusBadge } from "./Badges";
import { bookingClock, epochToWallInputs, todayServiceDay, wallClockToEpochSec } from "./format";
import { useCreateBooking } from "./mutations";
import { useServices } from "./queries";
import {
  bookingRejectionOf,
  hasAlternatives,
  isBlocked,
  isOverridable,
  rejectionMessageKey,
  type BookingRejection,
} from "./rejections";
import { MANUAL_ENTRY_CHANNELS, type BookingChannel } from "./status";
import {
  banner,
  fieldLabel,
  modalOverlay,
  modalPanel,
  primaryButton,
  secondaryButton,
  textInput,
} from "./styles";

export interface ManualEntryPrefill {
  serviceId?: string;
  startSec?: number;
  partySize?: number;
  guestGivenName?: string | null;
  guestFamilyName?: string | null;
  guestPhone?: string | null;
  guestEmail?: string | null;
  zonePreference?: string | null;
  specialRequests?: string | null;
  allergyNote?: string | null;
}

/**
 * Manual entry (R5): phone or ahead-of-arrival walk-in. It calls the SAME claim-backed create
 * endpoint the widget does — guest match-or-create and the blacklist gate run server-side, so a full
 * slot returns the same rejection + alternatives a diner gets, a BLOCKED guest stops the save with a
 * staff-facing reason, and a FLAGGED guest is created with a pre-save warning. Lead-time / horizon
 * blocks show the diner-equivalent message plus a MANAGER-only override. On success it names the
 * confirmation the guest will receive.
 */
export function ManualEntryDialog({
  onClose,
  onCreated,
  prefill,
  canOverride,
}: {
  onClose: () => void;
  onCreated: (b: StaffBookingResponse) => void;
  prefill?: ManualEntryPrefill;
  canOverride: boolean;
}) {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const services = useServices(venueId);
  const create = useCreateBooking(venueId);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  const initial = prefill?.startSec
    ? epochToWallInputs(prefill.startSec, venue.timeZone)
    : { date: todayServiceDay(venue.timeZone), time: "19:30" };

  const [serviceId, setServiceId] = useState(prefill?.serviceId ?? "");
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [partySize, setPartySize] = useState(prefill?.partySize ?? 2);
  const [channel, setChannel] = useState<BookingChannel>("PHONE");
  const [seatImmediately, setSeatImmediately] = useState(false);
  const [given, setGiven] = useState(prefill?.guestGivenName ?? "");
  const [family, setFamily] = useState(prefill?.guestFamilyName ?? "");
  const [phone, setPhone] = useState(prefill?.guestPhone ?? "");
  const [email, setEmail] = useState(prefill?.guestEmail ?? "");
  const [zonePreference, setZonePreference] = useState(prefill?.zonePreference ?? "");
  const [specialRequests, setSpecialRequests] = useState(prefill?.specialRequests ?? "");
  const [allergyNote, setAllergyNote] = useState(prefill?.allergyNote ?? "");

  const [rejection, setRejection] = useState<BookingRejection | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [created, setCreated] = useState<StaffBookingResponse | null>(null);

  // Default to the first service once loaded — derived directly during render (the condition becomes
  // false, and stays false, the instant `serviceId` is set, so there is no risk of a render loop).
  const firstService = services.data?.[0];
  if (!serviceId && firstService) setServiceId(firstService.id);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setRejection(null);
    setFieldErrors({});
    setBlockedMsg(null);
    setErrorMsg(null);
    const startSec = wallClockToEpochSec(date, time, venue.timeZone);
    const body: StaffCreateBookingRequest = {
      serviceId,
      startSec,
      partySize,
      channel,
      guest: {
        givenName: given || null,
        familyName: family || null,
        phone: phone || null,
        email: email || null,
      },
      language: venue.defaultLanguage || null,
      zonePreference: zonePreference || null,
      specialRequests: specialRequests || null,
      allergyNote: allergyNote || null,
      seatImmediately: channel === "WALK_IN" && seatImmediately,
      overrideReason: overrideMode && overrideReason ? overrideReason : null,
    };
    create.mutate(body, {
      onSuccess: (b) => setCreated(b),
      onError: (err) => {
        const rej = bookingRejectionOf(err);
        if (rej) {
          if (isBlocked(rej)) {
            setBlockedMsg(t("bookings.reject.GUEST_BLOCKED"));
            return;
          }
          setRejection(rej);
          if (isOverridable(rej) && canOverride) setOverrideMode(true);
          return;
        }
        const fe = fieldErrorsOf(err);
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe);
          return;
        }
        setErrorMsg(t("bookings.error.createFailed"));
      },
    });
  }

  function pickAlternative(startSec: number) {
    const w = epochToWallInputs(startSec, venue.timeZone);
    setDate(w.date);
    setTime(w.time);
    setRejection(null);
  }

  if (created) {
    return (
      <Overlay onClose={onClose} label={t("bookings.entry.createdTitle")}>
        <h2 ref={headingRef} tabIndex={-1} className={heading}>
          {t("bookings.entry.createdTitle")}
        </h2>
        <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
          <StatusBadge status={created.status} />
          <span className={css({ fontSize: "sm", color: "fg.muted" })}>
            {created.externalBookingId}
          </span>
        </div>
        {created.blacklistFlagged ? (
          <p role="alert" className={banner("warn")}>
            {t("bookings.entry.flaggedNotice")}
          </p>
        ) : null}
        <p className={banner("info")}>{t("bookings.entry.confirmationNotice")}</p>
        <div className={actions}>
          <button type="button" className={primaryButton} onClick={() => onCreated(created)}>
            {t("bookings.action.done")}
          </button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose} label={t("bookings.entry.title")}>
      <h2 ref={headingRef} tabIndex={-1} className={heading}>
        {t("bookings.entry.title")}
      </h2>
      <form
        onSubmit={submit}
        className={css({ display: "flex", flexDirection: "column", gap: "3" })}
      >
        {blockedMsg ? (
          <p role="alert" className={banner("error")}>
            {blockedMsg}
          </p>
        ) : null}
        {errorMsg ? (
          <p role="alert" className={banner("error")}>
            {errorMsg}
          </p>
        ) : null}
        {rejection ? (
          <RejectionNotice
            rejection={rejection}
            onPick={pickAlternative}
            tz={venue.timeZone}
            locale={i18n.language}
          />
        ) : null}

        <div className={grid2}>
          <label className={fieldLabel}>
            {t("bookings.field.service")}
            <select
              className={textInput}
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              required
            >
              {(services.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nameFr}
                </option>
              ))}
            </select>
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.channel")}
            <select
              className={textInput}
              value={channel}
              onChange={(e) => setChannel(e.target.value as BookingChannel)}
            >
              {MANUAL_ENTRY_CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`bookings.channel.${c}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={grid3}>
          <label className={fieldLabel}>
            {t("bookings.field.date")}
            <input
              type="date"
              className={textInput}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.time")}
            <input
              type="time"
              className={textInput}
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
            />
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.party")}
            <input
              type="number"
              min={1}
              max={500}
              className={textInput}
              value={partySize}
              onChange={(e) => setPartySize(Number(e.target.value))}
              required
            />
          </label>
        </div>

        {channel === "WALK_IN" ? (
          <label
            className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm" })}
          >
            <input
              type="checkbox"
              checked={seatImmediately}
              onChange={(e) => setSeatImmediately(e.target.checked)}
            />
            {t("bookings.field.seatImmediately")}
          </label>
        ) : null}

        <div className={grid2}>
          <label className={fieldLabel}>
            {t("bookings.field.givenName")}
            <input className={textInput} value={given} onChange={(e) => setGiven(e.target.value)} />
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.familyName")}
            <input
              className={textInput}
              value={family}
              onChange={(e) => setFamily(e.target.value)}
            />
          </label>
        </div>

        <div className={grid2}>
          <label className={fieldLabel}>
            {t("bookings.field.phone")}
            <input
              className={textInput}
              value={phone}
              inputMode="tel"
              placeholder="06 12 34 56 78"
              onChange={(e) => setPhone(e.target.value)}
            />
            {fieldErrors.guest ? <FieldError msg={fieldErrors.guest} /> : null}
          </label>
          <label className={fieldLabel}>
            {t("bookings.field.email")}
            <input
              type="email"
              className={textInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </div>

        {/* The optional context fields aren't needed for a routine "table for 2" — tuck them behind a
            disclosure so the common path is just service/time/party/guest, and open it automatically
            when a prefill already carried one so nothing is hidden. */}
        <details open={Boolean(zonePreference || specialRequests || allergyNote)}>
          <summary
            className={css({ cursor: "pointer", fontSize: "sm", color: "fg.muted", py: "1" })}
          >
            {t("bookings.entry.moreDetails")}
          </summary>
          <div className={css({ display: "flex", flexDirection: "column", gap: "3", pt: "2" })}>
            <label className={fieldLabel}>
              {t("bookings.field.zonePreference")}
              <input
                className={textInput}
                value={zonePreference}
                onChange={(e) => setZonePreference(e.target.value)}
              />
            </label>
            <label className={fieldLabel}>
              {t("bookings.field.specialRequests")}
              <input
                className={textInput}
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
              />
            </label>
            <label className={fieldLabel}>
              {t("bookings.field.allergyNote")}
              <input
                className={textInput}
                value={allergyNote}
                onChange={(e) => setAllergyNote(e.target.value)}
              />
            </label>
          </div>
        </details>

        {overrideMode ? (
          <div className={banner("warn")}>
            <span className={css({ fontWeight: "medium" })}>{t("bookings.override.title")}</span>
            <label className={fieldLabel}>
              {t("bookings.override.reason")}
              <input
                className={textInput}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </label>
          </div>
        ) : null}

        <div className={actions}>
          <button type="button" className={secondaryButton} onClick={onClose}>
            {t("bookings.action.cancel")}
          </button>
          <button type="submit" className={primaryButton} disabled={create.isPending || !serviceId}>
            {create.isPending ? t("bookings.action.saving") : t("bookings.entry.submit")}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

function RejectionNotice({
  rejection,
  onPick,
  tz,
  locale,
}: {
  rejection: BookingRejection;
  onPick: (startSec: number) => void;
  tz: string;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <div role="alert" className={banner("error")}>
      <span>{t(rejectionMessageKey(rejection))}</span>
      {rejection.largestFitParty !== undefined ? (
        <span>{t("bookings.reject.largestFit", { count: rejection.largestFitParty })}</span>
      ) : null}
      {hasAlternatives(rejection) ? (
        <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
          <span className={css({ fontWeight: "medium" })}>{t("bookings.reject.alternatives")}</span>
          <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
            {rejection.alternatives.map((a) => (
              <button
                key={a.startSec}
                type="button"
                className={secondaryButton}
                onClick={() => onPick(a.startSec)}
              >
                {bookingClock(a.startSec, tz, locale)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FieldError({ msg }: { msg: string }) {
  return (
    <span role="alert" className={css({ fontSize: "xs", color: "booking.noShow.fg" })}>
      {msg}
    </span>
  );
}

/** A focus-managed modal shell (Escape closes; heading gets focus via the caller). */
export function Overlay({
  children,
  onClose,
  label,
}: {
  children: React.ReactNode;
  onClose: () => void;
  label: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className={modalOverlay}>
      <dialog open aria-modal="true" aria-label={label} className={modalPanel}>
        {children}
      </dialog>
    </div>
  );
}

const heading = css({ fontSize: "lg", fontWeight: "bold", outline: "none" });
const actions = css({ display: "flex", gap: "2", justifyContent: "flex-end", flexWrap: "wrap" });
const grid2 = css({
  display: "grid",
  gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" },
  gap: "3",
});
const grid3 = css({
  display: "grid",
  gridTemplateColumns: { base: "1fr", sm: "1fr 1fr 1fr" },
  gap: "3",
});
